import React, {useContext, useRef, useEffect, useState} from 'react';
import parse, { HTMLReactParserOptions, domToReact, DOMNode } from 'html-react-parser';

import { getContentAd } from './Ad'
import { Article } from '../helper/interfaces';
import ArticleBodyTeaser from './ArticleBodyTeaser';
import { useLoggedRouter } from '../helper/useLoggedRouter';
import { ContentType } from '../helper/enums';
import { AppContext } from '../provider/AppContextProvider';

type BodyProps = {
  article: Article
  navigate?: (id?: string, ressortId?: string) => void
}

export default function EventBody(props: BodyProps) {
  const [routerPush] = useLoggedRouter();
  const { affiliateShow, setAffiliateShow } = useContext(AppContext);
  const affiliateButtonRef = useRef<HTMLButtonElement>(null);
  const closeIconRef = useRef<HTMLSpanElement>(null);
  const clickedButtonRef = useRef<HTMLElement | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  const calculatePosition = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const tooltipWidth = 300;
    let leftPosition = rect.left + window.scrollX - tooltipWidth / 2 + rect.width / 2;
    
    // Überprüfen, ob die Box über den rechten Rand hinausragt
    if (leftPosition + tooltipWidth > window.innerWidth) {
      leftPosition = window.innerWidth - tooltipWidth - 10;
    }
    
    // Überprüfen, ob die Box über den linken Rand hinausragt
    if (leftPosition < 10) {
      leftPosition = 10;
    }
    
    setTooltipPosition({
      top: rect.bottom + window.scrollY + 8,
      left: leftPosition
    });
  };

  const updateTooltipPosition = () => {
    if (clickedButtonRef.current) {
      calculatePosition(clickedButtonRef.current);
    } else if (closeIconRef.current) {
      calculatePosition(closeIconRef.current);
    }
  };

  // Update tooltip position when shown and on scroll/resize
  useEffect(() => {
    if (affiliateShow) {
      setIsAnimating(true);
      updateTooltipPosition();
      window.addEventListener('scroll', updateTooltipPosition);
      window.addEventListener('resize', updateTooltipPosition);
    } else {
      window.removeEventListener('scroll', updateTooltipPosition);
      window.removeEventListener('resize', updateTooltipPosition);
    }
    return () => {
      window.removeEventListener('scroll', updateTooltipPosition);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [affiliateShow]);

  // Focus the button when affiliate box is shown
  useEffect(() => {
    if (affiliateShow && affiliateButtonRef.current) {
      affiliateButtonRef.current.focus();
    }
  }, [affiliateShow]);

  // Handle ESC key to close affiliate box
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && affiliateShow) {
        handleCloseInfo();
      }
    };

    if (affiliateShow) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [affiliateShow, setAffiliateShow]);

  const handleOpenInfo = () => {
    setAffiliateShow(true);
  };

  const handleOpenInfoWithPosition = (e: React.MouseEvent<HTMLButtonElement>) => {
    const target = e.currentTarget;
    clickedButtonRef.current = target;
    calculatePosition(target);
    setAffiliateShow(true);
  };

  const handleCloseInfo = () => {
    setIsAnimating(false);
    setTimeout(() => {
      setAffiliateShow(false);
      clickedButtonRef.current = null;
    }, 300);
  };

  let bodyContent = props.article.body ? props.article.body : ""
  let re = /\(\(Platzhalter\)\)/g;
  bodyContent = bodyContent.replace(re, '<div id="ContentPlaceholder"></div>')
  re = /\(\(ContentAd[0-9]+\)\)/g;
  bodyContent = bodyContent.replace(re, '<div id="AdPlaceholder"></div>')
  re = /\(\(AdPlaceholder\)\)/g;
  bodyContent = bodyContent.replace(re, '<div id="AdPlaceholder"></div>')
  re = /\(\(ContentAd\)\)/g;
  bodyContent = bodyContent.replace(re, '<div id="AdPlaceholder"></div>')
  re = /\(\(Object\)\)/g;
  bodyContent = bodyContent.replace(re, '<div id="ContentPlaceholder"></div>')
  re = /\(\(Module\)\)/g;
  bodyContent = bodyContent.replace(re, '')//.replace(re, '<div id="ModulePlaceholder"></div>')
  //replace all other placeholders with empty text
  re = /\(\([^\)]+\)\)/g;
  bodyContent = bodyContent.replace(re, '')
  const bodyParts = (props.article && props.article.body) ? [props.article.body] : []
  let counter = 0
  let keyCounter = 0
  let adItemCounter = 1

  const internalNavigate = async (e ,id) => {
    e.stopPropagation();
    e.preventDefault();

    if (props.navigate) {
        props.navigate(id, undefined)
    }
    else {
      await routerPush(`/article/${encodeURIComponent(id)}`);
    }
  }

  const options: HTMLReactParserOptions = {
    replace: domNode => {
      if ('attribs' in domNode && (domNode?.attribs?.id || domNode?.attribs?.href || domNode?.attribs?.class) ) {
        if (domNode.attribs.id === 'ContentPlaceholder') {
          if(props.article.objectListTeasers.length > counter) {
            const teaser = props.article.objectListTeasers[counter++];
            if(teaser.type != ContentType.Unknown) {
              return <div>
                <ArticleBodyTeaser teaser={teaser} navigate={props.navigate}/>
              </div>
            }
            else {
              return <React.Fragment />
            }
          }
          else {
              return <React.Fragment />
          }
        }

        if (domNode.attribs.id === 'AdPlaceholder') {
          return getContentAd(++adItemCounter);
        }

        const internalNavigateRessort = async (e, id) => {
          e.stopPropagation();
          e.preventDefault();
      
          if (props.navigate) {
            props.navigate(undefined, id)
          }
          else {
            await routerPush(`/ressort/${encodeURIComponent(id)}`);
          }
        };

        if (domNode && domNode.attribs.href) {
          if(domNode.attribs.href.startsWith("detail:")) {
            const articleId = domNode.attribs.href.split(":")[1]
            return <a
                id={`a-internal-article-${articleId}`}
                href={`/article/${articleId}`}
                key={`teaser-link-${articleId}`}
                onClick={(e) => { internalNavigate(e, articleId) }}
              >
                {domToReact(domNode.children as DOMNode[], options)}
              </a>
          }
          else if(domNode.attribs.href.startsWith("ressort:")) {
            const ressortId = domNode.attribs.href.split(":")[1]
            return <a
                id={`a-internal-article-${ressortId}`}
                href={`/ressort/${ressortId}`}
                key={`teaser-link-${ressortId}`}
                onClick={(e) => { internalNavigateRessort(e, ressortId) }}
              >
                {domToReact(domNode.children as DOMNode[], options)}
              </a>
          }
          else if(domNode.attribs.href.startsWith("#")) {
            //do nothing, links in article
          }
          else if (domNode && domNode?.attribs?.class?.includes("nb-affiliate-link")) {
            return (
              <React.Fragment>
                <a
                  id={`a-internal-article-${domNode.attribs.href}`}
                  href={`${domNode.attribs.href}`}
                  key={`teaser-link-${domNode.attribs.href}`}
                  target = "_blank"
                  rel = "noreferrer"
                >
                  {domToReact(domNode.children as DOMNode[], options)}
                </a> <button className="icon-info inline-icon cursor-pointer" aria-label="Info" onClick={handleOpenInfoWithPosition}></button>
              </React.Fragment>)
          }
          else if (domNode && domNode.attribs.href) {
            domNode.attribs.target = "_blank"
          }
        }
      }
    }
  };

  return (
    <React.Fragment key={`body-event-body-nested-${counter}`}>
      {
        parse(bodyContent, options)
      }
      { affiliateShow && 
        <div 
          className={`affiliate-hint-box-tooltip ${isAnimating ? 'fade-in' : 'fade-out'}`}
          style={{
            position: 'absolute',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            width: `${Math.min(300, window.innerWidth - 20)}px`,
            maxWidth: '300px',
            zIndex: 30
          }}
        >
          <div className="text-right">
            <span 
              ref={closeIconRef}
              className="icon-cancel-circle cursor-pointer" 
              onClick={handleCloseInfo}
            ></span>
          </div>
          <div>
            <span className="icon-info inline-icon"></span> <strong>Affiliate-Link:</strong> Dieser Artikel enthält Affiliate-Links. Alle Produkte werden von der Redaktion unabhängig ausgewählt. Im Falle eines Kaufs des Produkts nach Klick auf den Link erhalten wir ggf. eine Provision. Mehr dazu lesen Sie <a className="nb-affiliate-link" onClick={(e) => {internalNavigate(e, '1.12475027')}}>hier</a>.
          </div>
        </div>
      }
    </React.Fragment>
  );
}